import { StaysPropertyFormOutput } from '@/schemas/stays/staysPropertySchema'
import {
  AspectRatio,
  Box,
  CloseButton,
  Image,
  InputProps,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { Upload } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Control, FieldValues, Path, useController } from 'react-hook-form'

interface SingleImageUploadProps<StaysPropertyFormInput extends FieldValues>
  extends Omit<InputProps, 'name'> {
  control: Control<StaysPropertyFormInput, any, StaysPropertyFormOutput>
  name: Path<StaysPropertyFormInput>
  maxImages?: number
  maxSizePerImage?: number // in MB
}

export const SingleImageUpload = <StaysPropertyFormInput extends FieldValues>({
  control,
  name,
  maxImages = 1,
  maxSizePerImage = 5,
}: SingleImageUploadProps<StaysPropertyFormInput>) => {
  const {
    field: { onChange },
  } = useController({
    name,
    control,
  })
  const toast = useToast()
  const [preview, setPreview] = useState<string | null>(null)
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      // Validate file sizes
      const oversizedFiles = acceptedFiles.filter(
        (file) => file.size > maxSizePerImage * 1024 * 1024
      )

      if (oversizedFiles.length > 0) {
        toast({
          title: 'Arquivos muito grandes',
          description: `Algumas imagens excedem ${maxSizePerImage}MB e foram ignoradas`,
          status: 'warning',
          duration: 5000,
        })
      }

      const validFiles = acceptedFiles.filter(
        (file) => file.size <= maxSizePerImage * 1024 * 1024
      )

      if (validFiles.length > maxImages) {
        toast({
          title: 'Muitas imagens',
          description: `Máximo de ${maxImages} imagem permitida`,
          status: 'warning',
          duration: 5000,
        })
        return
      }
      const url = URL.createObjectURL(validFiles[0])
      onChange([
        {
          url,
          is_primary: true,
          file: validFiles[0],
          stays_id: null,
        },
      ])
      setPreview(url)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxImages, maxSizePerImage, onChange]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
    multiple: false,
  })

  if (preview) {
    return (
      <Box position={'relative'}>
        <CloseButton
          onClick={() => {
            onChange([])
            setPreview(null)
          }}
          zIndex={20}
          position="absolute"
          top={2}
          right={2}
          bg={'red.500'}
          color={'white'}
          _hover={{ bg: 'red.600' }}
        />
        <AspectRatio maxH={'150px'} h={'full'} ratio={4 / 3}>
          <Image
            src={preview}
            alt="Preview"
            borderRadius="lg"
            objectFit="cover"
          />
        </AspectRatio>
      </Box>
    )
  }

  return (
    <Box
      {...getRootProps()}
      border="2px dashed"
      borderColor={isDragActive ? 'blue.400' : 'gray.300'}
      borderRadius="lg"
      p={8}
      textAlign="center"
      cursor="pointer"
      bg={isDragActive ? 'blue.50' : 'gray.50'}
      transition="all 0.2s"
      _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
    >
      <input {...getInputProps()} />
      <VStack spacing={2}>
        <Upload size={32} color={isDragActive ? '#3182CE' : '#A0ADB8'} />
        <Text
          fontWeight="medium"
          color={isDragActive ? 'blue.600' : 'gray.600'}
        >
          {isDragActive
            ? 'Solte as imagens aqui...'
            : 'Arraste a imagem ou clique para selecionar a imagem de capa da propriedade'}
        </Text>
      </VStack>
    </Box>
  )
}
